import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import PasswordInput from '../PasswordInput';
import { formatDateOnly, formatDateCompact } from '../../utils/dateUtils';
import { formatFileSize } from '../../utils/formatFileSize';

// Sorting helpers
const handleSort = (field, currentField, currentDirection, setField, setDirection) => {
  if (currentField === field) {
    setDirection(currentDirection === 'asc' ? 'desc' : 'asc');
  } else {
    setField(field);
    setDirection('asc');
  }
};

const getSortIcon = (field, currentField, currentDirection) => {
  if (currentField !== field) return null;
  return currentDirection === 'asc' ? ' ▲' : ' ▼';
};

const sortData = (data, field, direction) => {
  if (!field) return data;

  return [...data].sort((a, b) => {
    let aVal, bVal;

    if (field === 'roles') {
      aVal = a.roles.map(r => r.name).join(', ').toLowerCase();
      bVal = b.roles.map(r => r.name).join(', ').toLowerCase();
    } else if (field === 'created_at' || field === 'last_active_at') {
      aVal = new Date(a[field]);
      bVal = new Date(b[field]);
    } else if (field === 'reflection_count' || field === 'total_media_size') {
      aVal = parseInt(a[field]) || 0;
      bVal = parseInt(b[field]) || 0;
    } else if (field === 'storage_percent') {
      const aLimit = parseInt(a.plan_storage_limit) || 1;
      const bLimit = parseInt(b.plan_storage_limit) || 1;
      aVal = (parseInt(a.total_media_size) || 0) / aLimit;
      bVal = (parseInt(b.total_media_size) || 0) / bLimit;
    } else {
      aVal = a[field];
      bVal = b[field];
    }

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    if (aVal instanceof Date && bVal instanceof Date) {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    const stringA = String(aVal).toLowerCase();
    const stringB = String(bVal).toLowerCase();
    const comparison = stringA.localeCompare(stringB);
    return direction === 'asc' ? comparison : -comparison;
  });
};

function getUsageColor(ratio) {
  if (ratio >= 1.0) return '#ef4444';   // red - over limit
  if (ratio >= 0.9) return '#f59e0b';   // amber - warning
  return '#22c55e';                       // green - healthy
}

function AdminUsers({ timezone }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    roleIds: [],
    planId: null,
    firstTimeLogin: false,
    emailVerified: false,
    phoneVerified: false,
    emailNotificationsEnabled: true,
    smsConsent: false,
    smsRemindersEnabled: false,
    smsReminderFrequency: 'weekly',
    phone: '',
    transcriptionPreference: 'on_demand',
    authorizeAi: false,
    automateTitles: false
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchPlans();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Failed to fetch users: ${errorMsg}`);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/admin/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/admin/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      roleIds: user.roles.map(r => r.id),
      planId: user.plan_id || null,
      firstTimeLogin: user.first_time_login ?? false,
      emailVerified: user.email_verified ?? false,
      phoneVerified: user.phone_verified ?? false,
      emailNotificationsEnabled: user.email_notifications_enabled ?? true,
      smsConsent: user.sms_consent_given ?? false,
      smsRemindersEnabled: user.preferences?.sms_reminders_enabled === 'true',
      smsReminderFrequency: user.preferences?.sms_reminder_frequency || 'weekly',
      phone: user.phone || '',
      transcriptionPreference: user.preferences?.transcription_preference || 'on_demand',
      authorizeAi: user.preferences?.authorize_ai === 'true',
      automateTitles: user.preferences?.automate_titles === 'true'
    });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      roleIds: [],
      planId: null,
      firstTimeLogin: false,
      emailVerified: false,
      phoneVerified: false,
      emailNotificationsEnabled: true,
      smsConsent: false,
      smsRemindersEnabled: false,
      smsReminderFrequency: 'weekly',
      phone: '',
      transcriptionPreference: 'on_demand',
      authorizeAi: false,
      automateTitles: false
    });
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();

    try {
      const updateData = {
        username: formData.username,
        email: formData.email,
        roleIds: formData.roleIds,
        planId: formData.planId,
        firstTimeLogin: formData.firstTimeLogin,
        emailVerified: formData.emailVerified,
        phoneVerified: formData.phoneVerified,
        emailNotificationsEnabled: formData.emailNotificationsEnabled,
        smsConsentGiven: formData.smsConsent,
        phone: formData.phone || null,
        preferences: {
          sms_reminders_enabled: String(formData.smsRemindersEnabled),
          sms_reminder_frequency: formData.smsReminderFrequency,
          transcription_preference: formData.transcriptionPreference,
          authorize_ai: String(formData.authorizeAi),
          automate_titles: String(formData.automateTitles)
        }
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      await api.put(`/admin/users/${editingUser.id}`, updateData);

      fetchUsers();
      closeEditModal();
      alert('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      fetchUsers();
      alert('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleRole = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId]
    }));
  };

  return (
    <>
      <div className="admin-section">
        <h2>User Management</h2>
        <table className="admin-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort('username', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Username{getSortIcon('username', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('email', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Email{getSortIcon('email', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('roles', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Roles{getSortIcon('roles', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('reflection_count', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Reflections{getSortIcon('reflection_count', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('total_media_size', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Media{getSortIcon('total_media_size', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('plan_name', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Plan{getSortIcon('plan_name', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('storage_percent', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Usage{getSortIcon('storage_percent', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('created_at', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Joined{getSortIcon('created_at', sortField, sortDirection)}
              </th>
              <th
                onClick={() => handleSort('last_active_at', sortField, sortDirection, setSortField, setSortDirection)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Last Active{getSortIcon('last_active_at', sortField, sortDirection)}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortData(users, sortField, sortDirection).map(user => {
              const mediaSize = parseInt(user.total_media_size) || 0;
              const planLimit = parseInt(user.plan_storage_limit) || 0;
              const usageRatio = planLimit > 0 ? mediaSize / planLimit : 0;
              const usagePercent = Math.round(usageRatio * 100);

              return (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.roles.map(r => r.name).join(', ') || 'No roles'}
                  </td>
                  <td>{user.reflection_count || 0}</td>
                  <td>{formatFileSize(mediaSize)}</td>
                  <td>{user.plan_name || 'No plan'}</td>
                  <td>
                    {planLimit > 0 ? (
                      <div
                        className="storage-usage-bar"
                        title={`${formatFileSize(mediaSize)} / ${formatFileSize(planLimit)}`}
                      >
                        <div
                          className="storage-usage-fill"
                          style={{
                            width: `${Math.min(100, usagePercent)}%`,
                            backgroundColor: getUsageColor(usageRatio)
                          }}
                        />
                        <span className="storage-usage-text">{usagePercent}%</span>
                      </div>
                    ) : (
                      <span style={{ opacity: 0.5 }}>N/A</span>
                    )}
                  </td>
                  <td>{formatDateOnly(user.created_at, timezone)}</td>
                  <td>{user.last_active_at ? formatDateCompact(user.last_active_at, timezone) : 'Never'}</td>
                  <td>
                    <button
                      className="btn-edit"
                      onClick={() => openEditModal(user)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteUser(user.id, user.username)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editingUser && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit User: {editingUser.username}</h3>
              <button onClick={closeEditModal}>&#10005;</button>
            </div>

            <form onSubmit={handleSaveUser} className="admin-form compact-form">
              <div className="compact-row">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              <div className="compact-row">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="compact-row">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g. +15551234567"
                />
              </div>
              <div className="compact-row">
                <label>Password</label>
                <PasswordInput
                  name="userPassword"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Leave blank to keep current"
                />
              </div>

              <div className="compact-row">
                <label>Roles</label>
                <div className="compact-roles">
                  {roles.map(role => (
                    <label key={role.id} className="compact-check">
                      <input
                        type="checkbox"
                        checked={formData.roleIds.includes(role.id)}
                        onChange={() => toggleRole(role.id)}
                      />
                      <span>{role.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="compact-section-label">Subscription</div>
              <div className="compact-row">
                <label>Plan</label>
                <select
                  value={formData.planId || ''}
                  onChange={(e) => setFormData({ ...formData, planId: e.target.value ? parseInt(e.target.value) : null })}
                >
                  <option value="">No plan</option>
                  {plans.filter(p => p.is_active || p.id === formData.planId).map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({formatFileSize(parseInt(plan.storage_limit_bytes))})
                    </option>
                  ))}
                </select>
              </div>

              <div className="compact-section-label">Flags</div>
              <div className="compact-toggle-grid">
                <label className="compact-check">
                  <input type="checkbox" checked={formData.firstTimeLogin} onChange={(e) => setFormData({ ...formData, firstTimeLogin: e.target.checked })} />
                  <span>First time login</span>
                </label>
                <label className="compact-check">
                  <input type="checkbox" checked={formData.emailVerified} onChange={(e) => setFormData({ ...formData, emailVerified: e.target.checked })} />
                  <span>Email verified</span>
                </label>
                <label className="compact-check">
                  <input type="checkbox" checked={formData.phoneVerified} onChange={(e) => setFormData({ ...formData, phoneVerified: e.target.checked })} />
                  <span>Phone verified</span>
                </label>
              </div>

              <div className="compact-section-label">Notifications</div>
              <div className="compact-toggle-grid">
                <label className="compact-check">
                  <input type="checkbox" checked={formData.emailNotificationsEnabled} onChange={(e) => setFormData({ ...formData, emailNotificationsEnabled: e.target.checked })} />
                  <span>Email reminders</span>
                </label>
                <label className="compact-check">
                  <input type="checkbox" checked={formData.smsConsent} onChange={(e) => setFormData({ ...formData, smsConsent: e.target.checked })} />
                  <span>SMS consent</span>
                </label>
                <label className="compact-check">
                  <input type="checkbox" checked={formData.smsRemindersEnabled} onChange={(e) => setFormData({ ...formData, smsRemindersEnabled: e.target.checked })} />
                  <span>SMS reminders</span>
                </label>
              </div>
              <div className="compact-row">
                <label>SMS frequency</label>
                <select value={formData.smsReminderFrequency} onChange={(e) => setFormData({ ...formData, smsReminderFrequency: e.target.value })}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div className="compact-section-label">AI &amp; Transcription</div>
              <div className="compact-toggle-grid">
                <label className="compact-check">
                  <input type="checkbox" checked={formData.authorizeAi} onChange={(e) => setFormData({ ...formData, authorizeAi: e.target.checked })} />
                  <span>AI authorized</span>
                </label>
                <label className="compact-check">
                  <input type="checkbox" checked={formData.automateTitles} onChange={(e) => setFormData({ ...formData, automateTitles: e.target.checked })} />
                  <span>Auto-titles</span>
                </label>
              </div>
              <div className="compact-row">
                <label>Transcription</label>
                <select value={formData.transcriptionPreference} onChange={(e) => setFormData({ ...formData, transcriptionPreference: e.target.value })}>
                  <option value="on_demand">On demand</option>
                  <option value="automatic">Automatic</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-save">Save</button>
                <button type="button" onClick={closeEditModal} className="btn-cancel">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminUsers;
